import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import { useMMAuth } from '@machinemetrics/mm-react-tools';
import { DEFAULT_COMPANY } from '../../constants/company';
import { machineLabel } from '../../utils/machineLabel';
import { statusOf, groupByType, alarmOf } from '../../utils/floorStatus';
import {
  getAlarmCatalog, injectAlarm, clearAlarm,
  requestToolChange, finishToolChange, finishSetup,
} from '../../services/alarmApi';
import { listTemplates, dispatchScenario } from '../../services/planningApi';
import { getParts } from '../../services/erpApi';
import { sandboxCapacity } from '../../utils/sandboxCapacity';
import { typeLabel } from '../../constants/machineTypes';
import { fetchSaved, createSaved, migrateLegacyLocal, demandOverrideFromSaved, policyFromSaved } from '../../utils/sandboxScenarios';
import { useServerClock, SKEW_WARN_MS, describeSkew } from '../../utils/serverClock';
import TabletPendant from '../../components/hmi/TabletPendant';

// ── Floor Control (tablet) ───────────────────────────────────────────────────
// The DOING surface for the floor, where Live Floor is the READING surface. Same data, same status
// derivation (`statusOf` — one vocabulary across both pages, on purpose), but built for a thumb on
// an iPad standing at a booth rather than a cursor on a desk.
//
// What that changes, concretely:
//
//   TOUCH TARGETS. Every control here is >= 52px tall. The Live Floor's tool-change affordance is a
//   20px `🔧 change` chip with a `title=` tooltip — fine with a mouse, unhittable and unexplained
//   with a finger. Nothing on this page depends on hover, and nothing on it is smaller than a
//   fingertip (~9mm ≈ 44px at 1x, per Apple/Material; 52 buys margin for a moving hand).
//
//   ONE PRIMARY ACTION PER MACHINE. A tile shows the action the machine's CURRENT phase calls for as
//   a full-width filled button — Finish tool change / Finish setup / Clear alarm — and demotes
//   everything else to a secondary row. The operator does not choose from a menu; they confirm the
//   obvious next move. When no phase-specific action applies the primary slot stays empty rather
//   than inventing one (a disabled button that never enables is noise).
//
//   CONFIRMATION IS IN THE BUTTON, NOT A MODAL. Anything that mutates the floor irreversibly
//   (inject an alarm, dispatch a scenario, reset dispatch) arms on first tap and fires on second,
//   disarming after 4s. A modal costs a second screen and a second precise tap; arming costs one tap
//   in the place the finger already is. The "finish now" actions are deliberately SINGLE tap: they
//   are the speed-up controls this page exists for, and they only ever skip a wait.
//
//   NO SIDEBAR. Mounted outside ChentronixLayout — 220px of chrome is ~30% of an iPad portrait
//   viewport, and a booth operator navigates by handing over the tablet, not by browsing.
//
// Dispatch lives here too ("dispatch to the floor separately"): the Capacity Sandbox is a planning
// instrument you cannot drive standing up, but re-launching a SAVED scenario is one tap and is the
// thing a demo actually needs between runs. Authoring stays in the Sandbox; this only re-fires what
// the Sandbox already saved.

const API_BASE = '/machinesim/api';
const SOCKET_PATH = '/machinesim/socket.io';

// ── Design tokens ────────────────────────────────────────────────────────────
// Named here rather than sprinkled, because this page's whole contract is "an action's colour tells
// you what it does before you read it". See docs/tablet-control-intake.md.
const T = {
  bg: 'var(--cn-bg)',
  surface: 'var(--cn-surface)',
  surface2: 'var(--cn-surface2)',
  text: 'var(--cn-text)',
  muted: 'var(--cn-muted)',
  dimmed: 'var(--cn-dimmed)',
  gold: 'var(--cn-gold)',
  success: 'var(--cn-success)',
  warning: 'var(--cn-warning)',
  danger: 'var(--cn-danger)',
  hairline: 'rgba(168,144,96,0.14)',
  onFilled: '#10182a',   // navy ink on any filled (gold/green/amber/red) button
  tap: 52,               // minimum touch target, px
};

const indexLive = (payload) => {
  const out = {};
  for (const lm of Array.isArray(payload) ? payload : []) if (lm?.machine) out[lm.machine] = lm;
  return out;
};

const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

export default function FloorControlPage() {
  const [searchParams] = useSearchParams();
  const companySlug = searchParams.get('company') || DEFAULT_COMPANY;
  const auth = useMMAuth();
  const accessToken = auth?.accessToken ?? null;

  const [machines, setMachines] = useState([]);
  const [liveByName, setLiveByName] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [tab, setTab] = useState('floor');      // 'floor' | 'plan'
  const [sheet, setSheet] = useState(null);     // { kind:'tools'|'alarm'|'hmi', row? }
  const [toast, setToast] = useState(null);     // { text, tone }
  const socketRef = useRef(null);

  // One shared 1s clock, anchored to the SERVER's time rather than this device's. A tablet with a
  // fast clock made every server-issued deadline look expired and showed 0:00 on a setup the desktop
  // was counting down correctly — see utils/serverClock.js.
  const { now, skewMs, observe } = useServerClock();

  // Toast: the only feedback channel on this page. A tap that mutates the floor must say something
  // even when the resulting state change takes a socket round-trip to appear on the tile.
  const say = useCallback((text, tone = 'ok') => setToast({ text, tone, at: Date.now() }), []);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(id);
  }, [toast]);

  // REST hydrate + slow poll fallback, exactly as Live Floor: `/machines` is the roster spine and
  // `/floor/live` an overlay, so a missing engine costs the overlay and not the page.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const fetchAll = async () => {
      try {
        const sentAt = Date.now();
        const [mRes, lRes] = await Promise.all([
          axios.get(`${API_BASE}/machines?company=${companySlug}`),
          axios.get(`${API_BASE}/floor/live?company=${companySlug}`).catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        observe(mRes, sentAt);
        setMachines(mRes.data.machines || []);
        setLiveByName(indexLive(lRes.data));
        setError(null);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e?.message || 'Failed to load floor');
        setLoading(false);
      }
    };
    fetchAll();
    const id = setInterval(fetchAll, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [companySlug, observe]);

  useEffect(() => {
    const socket = io({ path: SOCKET_PATH, transports: ['websocket', 'polling'], reconnection: true });
    socketRef.current = socket;
    const join = () => socket.emit('joinCompany', companySlug);
    socket.on('connect', () => { setIsConnected(true); join(); });
    socket.on('disconnect', () => setIsConnected(false));
    socket.on('floorLiveUpdate', (payload, company) => {
      if (company && company !== companySlug) return;   // never let another tenant swap these cards
      if (Array.isArray(payload)) setLiveByName(indexLive(payload));
    });
    if (socket.connected) join();
    return () => socket.disconnect();
  }, [companySlug]);

  const rows = machines.map((m) => {
    const live = liveByName[m.name] || null;
    return {
      name: m.name,
      label: machineLabel(m.name, companySlug, m.machineType),
      type: String(m.machineType || live?.type || '').toUpperCase(),
      state: m.state || {},
      live,
      status: statusOf(live, m.state),
    };
  });

  const groups = groupByType(rows);
  const ordered = groups.flatMap((g) => g.machines.map((m) => ({ ...m, color: g.color })));
  const countOf = (label) => rows.filter((r) => r.status.label === label).length;

  // The sheet's row must track live data, not the snapshot taken when it opened — a tool-change
  // sheet left open while the machine enters its hold has to re-render with the hold.
  const sheetRow = sheet?.row ? (ordered.find((r) => r.name === sheet.row.name) || sheet.row) : null;

  return (
    <div style={{
      minHeight: '100vh', background: T.bg, color: T.text,
      // `manipulation` kills the 300ms double-tap-zoom delay iOS otherwise adds to every tap.
      touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
      paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
    }}>
      {/* Sticky bar: identity, link back to the reading view, connection, and the one global action.
          Sticky because the grid scrolls past a screen on any tablet and Dispatch must never require
          scrolling to the top to reach. */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20, background: T.bg,
        borderBottom: `1px solid ${T.hairline}`,
        padding: 'calc(12px + env(safe-area-inset-top, 0px)) 16px 12px',
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: T.gold, fontWeight: 700 }}>
            {companySlug} · control
          </div>
          <div style={{ fontSize: 21, fontWeight: 800, lineHeight: 1.2 }}>Floor Control</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: isConnected ? T.success : T.muted }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor' }} />
          {isConnected ? 'Live' : 'Polling'}
        </div>

        {/* A device clock this far out is not round-trip noise — the tablet itself is set wrong.
            Countdowns are already corrected against the server; this says so, because "the numbers
            are right but your iPad's date is wrong" is otherwise undiagnosable from the floor. */}
        {Math.abs(skewMs) > SKEW_WARN_MS && (
          <span title="Countdowns are corrected to server time. Fix the device clock in Settings → General → Date & Time."
            style={{ fontSize: 11, fontWeight: 700, color: T.warning, border: `1px solid ${T.warning}`, borderRadius: 999, padding: '3px 9px' }}>
            ⏱ device clock {describeSkew(skewMs)}
          </span>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link
            to={companySlug !== DEFAULT_COMPANY ? { pathname: '/', search: `?company=${companySlug}` } : '/'}
            style={{ textDecoration: 'none' }}
          >
            <TouchBtn variant="ghost" compact as="span">Live Floor ↗</TouchBtn>
          </Link>
        </div>

        {/* Two tabs, full width on their own row — the whole app on this device is "watch the floor"
            and "decide what it runs next", and they are peers. A segmented control rather than a
            nav: there is no third destination, and an overflow menu for two items is a tap tax. */}
        <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 4 }}>
          <TouchBtn full variant={tab === 'floor' ? 'gold' : 'ghost'} onPress={() => setTab('floor')}>
            Floor
          </TouchBtn>
          <TouchBtn full variant={tab === 'plan' ? 'gold' : 'ghost'} onPress={() => setTab('plan')}>
            Plan
          </TouchBtn>
        </div>
      </div>

      {tab === 'plan' ? (
        <PlanTab company={companySlug} token={accessToken} say={say} onDispatched={() => setTab('floor')} />
      ) : (
      <>
      {/* Counters. Same derivation as the tiles' badges, so a chip can never disagree with what it
          is counting — the invariant Live Floor already holds. Scrolls horizontally rather than
          wrapping to a second row on a narrow tablet, which would push the grid below the fold. */}
      <div style={{ display: 'flex', gap: 9, padding: '12px 16px 4px', overflowX: 'auto' }}>
        <Stat label="Running"  value={countOf('RUNNING')}     color={T.success} />
        <Stat label="Setup"    value={countOf('SETUP')}       color={T.gold} />
        <Stat label="Tool chg" value={countOf('TOOL CHANGE')} color={T.warning} />
        <Stat label="Starved"  value={countOf('STARVED')}     color={T.warning} />
        <Stat label="Faulted"  value={countOf('FAULTED')}     color={T.danger} />
        <Stat label="Idle"     value={countOf('IDLE')}        color={T.dimmed} />
      </div>

      {error && (
        <div style={{ margin: '12px 16px', padding: '13px 15px', borderRadius: 10, fontSize: 13,
          background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.4)', color: '#e8a89f' }}>
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div style={{ padding: '64px 0', textAlign: 'center', color: T.muted }}>Loading floor…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '64px 16px', textAlign: 'center', color: T.muted }}>
          No machines configured for “{companySlug}”.
        </div>
      ) : (
        // 320px min → 2 columns on iPad portrait (768), 3 on landscape (1024), 1 on a phone. No
        // breakpoints: the column count follows the viewport instead of a guessed device list.
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 14, padding: '10px 16px', alignItems: 'start',
        }}>
          {ordered.map((row) => (
            <MachineTile
              key={row.name}
              row={row}
              now={now}
              company={companySlug}
              say={say}
              onTools={() => setSheet({ kind: 'tools', row })}
              onAlarm={() => setSheet({ kind: 'alarm', row })}
              onOpen={() => setSheet({ kind: 'hmi', row })}
            />
          ))}
        </div>
      )}
      </>
      )}

      {/* The machine's own control, full-bleed. Not a Sheet: a CRT wants the whole screen, and the
          bottom-sheet chrome would read as app furniture wrapped around a pendant. */}
      {sheet?.kind === 'hmi' && sheetRow && (
        <TabletPendant row={sheetRow} company={companySlug} say={say} onClose={() => setSheet(null)} />
      )}

      {sheet?.kind === 'tools' && sheetRow && (
        <Sheet title={`${sheetRow.label} · tools`} onClose={() => setSheet(null)}>
          <ToolsSheet row={sheetRow} company={companySlug} say={say} />
        </Sheet>
      )}
      {sheet?.kind === 'alarm' && sheetRow && (
        <Sheet title={`${sheetRow.label} · alarm`} onClose={() => setSheet(null)}>
          <AlarmSheet row={sheetRow} company={companySlug} say={say} onDone={() => setSheet(null)} />
        </Sheet>
      )}

      {toast && <Toast toast={toast} />}
    </div>
  );
}

// ── Machine tile ─────────────────────────────────────────────────────────────
// Reads top-down in priority order: who am I → what am I doing → what can you do about it. The
// primary action is LAST because that is where the thumb rests when the tablet is held, and because
// it must sit below the state it acts on (you read the hold, then end it).
function MachineTile({ row, now, company, say, onTools, onAlarm, onOpen }) {
  const { name, label, live, status, color } = row;
  const r = live?.running;
  const tc = live?.toolChange || null;
  const faulted = !!r?.faulted;

  const partCount = r?.partCount ?? row.state.total_part_count ?? 0;
  const targetQty = r?.targetQty ?? live?.dispatched?.targetQty ?? row.state.target_qty ?? 0;
  const wo = r?.workOrderId || live?.dispatched?.workOrderId || row.state.work_order_id || null;

  const tcEndMs = tc?.endsAt ? new Date(tc.endsAt).getTime() : null;
  const tcLeft = tc ? (Number.isFinite(tcEndMs) ? Math.max(0, Math.round((tcEndMs - now) / 1000)) : Math.max(0, tc.remainingSec ?? 0)) : null;

  const isSetup = status.label === 'SETUP';
  const setupEndMs = r?.phaseEndsAt ? new Date(r.phaseEndsAt).getTime() : null;
  const setupLeft = (isSetup && setupEndMs) ? Math.max(0, Math.round((setupEndMs - now) / 1000)) : null;

  // A real CNC alarm, as opposed to the engine's normal upstream-starve hold — which is not an alarm
  // an operator can do anything about and is already said plainly by the STARVED badge. A WARNING
  // (e.g. 2015 DOOR OPEN) leaves `faulted` false and the machine cutting, which is exactly why
  // gating this on `faulted` hid it: the alarm was live on the wire and invisible on the floor.
  const alarm = alarmOf(live, row.state);

  const [busy, setBusy] = useState(null);   // which action is in flight

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); say(okMsg); }
    catch (e) { say(e.message || 'Failed', 'err'); }
    finally { setBusy(null); }
  };

  // Exactly one primary action, chosen by phase. Precedence mirrors `statusOf`: a fault outranks
  // everything, a tool-change hold outranks the phase it interrupted.
  // Clearing an alarm is NOT the primary slot: it lives in the alarm banner, next to the thing it
  // acts on, and an alarm can coexist with any phase (LATHE-01 ran a whole setup with 2015 DOOR OPEN
  // live). The primary stays the phase's own next move.
  let primary = null;
  if (tc) {
    primary = { key: 'ftc', label: '⏭ Finish tool change', variant: 'warning',
      fn: () => finishToolChange(name, company), ok: `${label}: tool change finished` };
  } else if (isSetup) {
    primary = { key: 'fsu', label: '⏭ Finish setup', variant: 'gold',
      fn: () => finishSetup(name, company), ok: `${label}: setup finished` };
  }

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.hairline}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ height: 4, background: color }} />
      <div style={{ padding: 14 }}>
        {/* The header IS the button that opens this machine's pendant — a 52px-tall target spanning
            the tile, which is both the most obvious thing to tap and the hardest to miss. The chevron
            is there because a tappable region with no affordance is a region nobody taps. */}
        <button
          type="button"
          onClick={onOpen}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            width: '100%', minHeight: T.tap, marginBottom: 8, padding: 0,
            background: 'transparent', border: 'none', color: T.text, fontFamily: 'inherit',
            cursor: 'pointer', textAlign: 'left', touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <span title={name} style={{ fontSize: 18, fontWeight: 800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{
              fontSize: 11, fontWeight: 800, letterSpacing: '0.07em', padding: '5px 10px',
              borderRadius: 999, border: `1px solid ${status.color}`, color: status.color, background: T.surface2,
            }}>
              {status.label}
            </span>
            <span aria-hidden="true" style={{ color: T.dimmed, fontSize: 18, lineHeight: 1 }}>›</span>
          </span>
        </button>

        {alarm && (
          <AlarmBanner alarm={alarm} faulted={faulted} busy={busy === 'clear'}
            onClear={() => run('clear', () => clearAlarm(name, company), `${label}: alarm cleared`)} />
        )}

        {/* One line of context, never more. Anything deeper belongs on Live Floor — a control surface
            that also tries to be a report ends up bad at both. */}
        <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 10, minHeight: 18 }}>
          {tc
            ? <span style={{ color: T.warning, fontWeight: 700 }}>
                🔧 {(tc.toolsChanging || []).join(', ') || 'tools'} · resumes in{' '}
                <span style={{ fontFamily: '"Courier New", monospace' }}>{mmss(tcLeft ?? 0)}</span>
              </span>
            : isSetup
              ? <span style={{ color: T.gold, fontWeight: 700 }}>
                  Setting up{setupLeft != null ? ` · ${mmss(setupLeft)} left` : ''}
                </span>
              : status.label === 'STARVED'
                ? <span style={{ color: T.warning }}>Waiting for upstream parts</span>
                : wo
                  ? <span><span style={{ color: T.gold, fontWeight: 700 }}>{wo}</span>{targetQty > 0 ? ` · ${partCount}/${targetQty}` : ''}</span>
                  : <span style={{ color: T.dimmed }}>No work order queued</span>}
        </div>

        {targetQty > 0 && (
          <div style={{ height: 5, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{ height: '100%', width: `${Math.min(100, (partCount / targetQty) * 100)}%`, background: color, transition: 'width .3s' }} />
          </div>
        )}

        {primary && (
          <div style={{ marginBottom: 9 }}>
            <TouchBtn variant={primary.variant} full busy={busy === primary.key}
              onPress={() => run(primary.key, primary.fn, primary.ok)}>
              {primary.label}
            </TouchBtn>
          </div>
        )}

        {/* Secondary row: always the same two, always in the same place, so muscle memory survives a
            phase change. Tools carries a badge when something is due — the reason to open it. */}
        <div style={{ display: 'flex', gap: 9 }}>
          <TouchBtn variant="ghost" full onPress={onTools}>
            🔧 Tools{dueCount(live) > 0 ? ` · ${dueCount(live)}` : ''}
          </TouchBtn>
          <TouchBtn variant="ghost" full onPress={onAlarm}>⚠ Alarm</TouchBtn>
        </div>
      </div>
    </div>
  );
}

// Alarm + its remedy in one block. Clear is a single tap: it is restorative, and an alarm cleared by
// mistake simply re-fires if the condition is still true.
function AlarmBanner({ alarm, faulted, busy, onClear }) {
  const fault = faulted || alarm.level === 'FAULT';
  const col = fault ? T.danger : T.warning;
  return (
    <div style={{
      marginBottom: 10, padding: '10px 12px', borderRadius: 11,
      border: `1px solid ${col}`, background: fault ? 'rgba(192,57,43,0.12)' : 'rgba(183,121,31,0.12)',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 9 }}>
        <span style={{ fontSize: 15 }}>{fault ? '⛔' : '⚠'}</span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 800, color: col, letterSpacing: '0.03em' }}>
            {alarm.code}
            {!fault && <span style={{ fontWeight: 600, color: T.muted, letterSpacing: 0 }}> · warning</span>}
          </span>
          {alarm.message && (
            <span style={{ display: 'block', fontSize: 12, color: T.text, marginTop: 2, lineHeight: 1.4 }}>
              {alarm.message}
            </span>
          )}
        </span>
      </div>
      <TouchBtn variant="success" full busy={busy} onPress={onClear}>✓ Clear alarm</TouchBtn>
    </div>
  );
}

// Tools at or past their preset — the badge that justifies opening the Tools sheet.
function dueCount(live) {
  if (!Array.isArray(live?.tools)) return 0;
  return live.tools.filter((t) => (t.pct ?? 0) >= 100 || t.retireRequested).length;
}

// ── Tools sheet ──────────────────────────────────────────────────────────────
// The full tool-change loop in one place: request a change (queued to the next part boundary), watch
// the hold, end the hold early. On Live Floor these three live in three different widgets at three
// sizes; here they are one list with one button size.
function ToolsSheet({ row, company, say }) {
  const { name, label, live } = row;
  const tools = Array.isArray(live?.tools) ? live.tools : [];
  const tc = live?.toolChange || null;
  const [busy, setBusy] = useState(null);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); say(okMsg); }
    catch (e) { say(e.message || 'Failed', 'err'); }
    finally { setBusy(null); }
  };

  return (
    <div>
      {tc && (
        <div style={{ marginBottom: 14, padding: 13, borderRadius: 12, background: 'rgba(224,168,64,0.1)', border: `1px solid ${T.warning}` }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: T.warning, marginBottom: 9 }}>
            IN TOOL CHANGE · {(tc.toolsChanging || []).join(', ') || '—'}
          </div>
          {/* Single tap on purpose. This is the speed-up control — the wait it skips is the cost. */}
          <TouchBtn variant="warning" full busy={busy === 'finish'}
            onPress={() => run('finish', () => finishToolChange(name, company), `${label}: tool change finished`)}>
            ⏭ Finish tool change now
          </TouchBtn>
        </div>
      )}

      {tools.length === 0 ? (
        <div style={{ color: T.muted, fontSize: 13, padding: '18px 0' }}>No tooling configured on this machine.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tools.map((t) => {
            const pct = Math.max(0, Math.min(100, t.pct ?? 0));
            const col = pct >= 100 ? T.danger : pct >= 80 ? T.warning : T.success;
            const changing = tc?.toolsChanging?.includes(t.key);
            const queued = !!t.retireRequested && !changing;
            return (
              <div key={t.key} style={{ padding: 12, borderRadius: 12, background: T.surface2, border: `1px solid ${T.hairline}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{changing ? '↻ ' : ''}{t.key}</span>
                  <span style={{ fontFamily: '"Courier New", monospace', fontSize: 13, color: T.muted }}>
                    {t.count}{t.preset ? ` / ${t.preset}` : ''} <span style={{ color: col, fontWeight: 700 }}>({pct}%)</span>
                  </span>
                </div>
                <div style={{ height: 5, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: col }} />
                </div>
                {changing ? (
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: T.warning }}>Changing now — use “Finish tool change” above.</div>
                ) : queued ? (
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: T.warning }}>⏳ Queued — fires at the next completed part.</div>
                ) : (
                  // Armed confirm: a request changes what the machine does next, and a stray tap in
                  // a pocket-height list is easy. The hold it triggers is not instantly undoable.
                  <TouchBtn variant="ghost" full busy={busy === t.key} confirm="Tap again to request"
                    onPress={() => run(t.key, () => requestToolChange(name, t.key, company), `${label}: ${t.key} change requested`)}>
                    🔧 Request change · {t.key}
                  </TouchBtn>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Alarm sheet ──────────────────────────────────────────────────────────────
// Catalog picker as a LIST of big rows rather than a <select>. A native select on iOS opens a wheel
// that covers the sheet and hides which machine you are acting on; the list keeps the header, the
// machine name and the choice on screen together.
function AlarmSheet({ row, company, say, onDone }) {
  const { name, label, live } = row;
  // Same rule as the tile: an alarm is clearable whenever one is present, not only when it halted
  // the machine. A WARNING leaves `faulted` false and used to leave the operator with no way out.
  const present = alarmOf(live, row.state);
  const [catalog, setCatalog] = useState([]);
  const [sel, setSel] = useState(null);          // index into catalog, or 'custom'
  const [custom, setCustom] = useState({ code: '', message: '', level: 'FAULT' });
  const [busy, setBusy] = useState(null);

  useEffect(() => { getAlarmCatalog().then(setCatalog).catch(() => {}); }, []);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); say(okMsg); onDone?.(); }
    catch (e) { say(e.message || 'Failed', 'err'); }
    finally { setBusy(null); }
  };

  const chosen = sel === 'custom'
    ? { code: custom.code.trim(), message: custom.message.trim(), level: custom.level }
    : (catalog[sel] ? { code: catalog[sel].code, message: catalog[sel].message, level: catalog[sel].level } : null);
  const canInject = !!chosen && (!!chosen.code || !!chosen.message);

  return (
    <div>
      {present && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 8 }}>
            Present: <strong style={{ color: T.text }}>{present.code}</strong>
            {present.message ? ` — ${present.message}` : ''}
          </div>
          <TouchBtn variant="success" full busy={busy === 'clear'}
            onPress={() => run('clear', () => clearAlarm(name, company), `${label}: alarm cleared`)}>
            ✓ Clear alarm on {label}
          </TouchBtn>
        </div>
      )}

      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.dimmed, marginBottom: 9 }}>
        Inject alarm
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {catalog.map((a, i) => (
          <PickRow key={i} selected={sel === i} onPress={() => setSel(i)}
            left={a.level === 'FAULT' ? '⛔' : '⚠'}
            title={a.code}
            sub={a.message}
            tone={a.level === 'FAULT' ? T.danger : T.warning}
          />
        ))}
        <PickRow selected={sel === 'custom'} onPress={() => setSel('custom')} left="✎" title="Custom…" sub="Any code, message and level" tone={T.muted} />
      </div>

      {sel === 'custom' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 14 }}>
          <input placeholder="code (e.g. SV0401)" value={custom.code}
            onChange={(e) => setCustom({ ...custom, code: e.target.value })} style={inputStyle} />
          <input placeholder="message" value={custom.message}
            onChange={(e) => setCustom({ ...custom, message: e.target.value })} style={inputStyle} />
          <div style={{ display: 'flex', gap: 9 }}>
            {['FAULT', 'WARNING'].map((lv) => (
              <TouchBtn key={lv} full variant={custom.level === lv ? 'gold' : 'ghost'}
                onPress={() => setCustom({ ...custom, level: lv })}>{lv}</TouchBtn>
            ))}
          </div>
        </div>
      )}

      {/* Armed confirm — injecting a FAULT halts the machine and shows up in MachineMetrics. */}
      <TouchBtn variant="danger" full disabled={!canInject} busy={busy === 'inject'} confirm="Tap again to inject"
        onPress={() => run('inject', () => injectAlarm(name, chosen, company), `${label}: ${chosen.code || chosen.message} injected`)}>
        ⚠ Inject on {label}
      </TouchBtn>
    </div>
  );
}

// ── Plan tab ─────────────────────────────────────────────────────────────────
// The Capacity Sandbox's four load-bearing levers, and nothing else.
//
// The Sandbox is a planning instrument with ~15 controls — demand per part, hypothetical station
// counts, per-stage cycle multipliers, per-edge transfer overrides, per-tool life presets, real-time
// mode, save/compare/fork. It is the right tool sitting down. Standing at a booth mid-sentence, four
// of those levers decide what the audience actually sees in the next ten minutes:
//
//   DEMAND        how much work exists at all.
//   CASTINGS      whether the lathes start cutting at t=0 or wait on the foundry. This is the one
//                 people reach for and the one whose units are least obvious, so it carries a live
//                 readout of how many lathes the seed actually covers.
//   TRANSFER %    when the MILLS start. It sets the bin size a downstream op waits to fill, so at
//                 25% of a 978-part lot the first mill waits ~7h and the floor looks broken; at 2%
//                 it waits ~35min. This is the single most demo-visible number in the app and it is
//                 buried three panels deep in the Sandbox.
//   REAL-TIME     whether programs run at their true compiled length, which is what makes the FOCAS
//                 block/line sweep look real to someone watching MachineMetrics next to it.
//
// What is deliberately NOT here: station counts and cycle multipliers. Station edits are
// sizing-only — they change the capacity math and are never dispatched — so a tablet control for
// them would imply a floor change that cannot happen. Authoring anything more than the four levers
// stays in the Sandbox; this tab and that page write the SAME saved-scenario store and dispatch
// through the SAME `policyFromSaved` builder, so neither can drift from the other.
function PlanTab({ company, token, say, onDispatched }) {
  const [template, setTemplate] = useState(null);
  const [parts, setParts] = useState([]);
  const [stations, setStations] = useState({});
  const [demand, setDemand] = useState({});
  const [castings, setCastings] = useState({});
  const [xferPct, setXferPct] = useState(25);
  const [realTime, setRealTime] = useState(false);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const [templates, allParts, rows] = await Promise.all([
          listTemplates(company, token), getParts(company), fetchSaved(company, token),
        ]);
        const tmpl = templates[0] || null;
        if (!alive) return;
        setSaved(rows);
        if (!tmpl) { setTemplate(null); setLoading(false); return; }
        // Scope to the parts this template actually plans — `getParts` returns the whole catalog.
        const ids = new Set((tmpl.demandBaseline || []).map((d) => d.partId));
        setTemplate(tmpl);
        setParts(allParts.filter((p) => ids.has(p.id)));
        setDemand(Object.fromEntries((tmpl.demandBaseline || []).map((d) => [d.partId, d.weeklyQty])));
        setStations(Object.fromEntries((tmpl.machineRoster || []).map((r) => [r.resourceGroup, r.count])));
        // Anything still in this browser's pre-server store goes up once, idempotent by name. Failing
        // to migrate must not fail the page — the plan is still authorable without the old scenarios.
        try {
          if (await migrateLegacyLocal(company, token, rows) > 0 && alive) setSaved(await fetchSaved(company, token));
        } catch { /* keep the legacy rows; they are not deleted */ }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.error || e?.message || 'Could not load the plan');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [company, token]);

  // Same pure feasibility math the Sandbox meters run on — required seconds per stage over a 24/7
  // week. Station counts come from the template roster and are NOT editable here (see the header).
  const cap = useMemo(
    () => sandboxCapacity({ parts, demand, stations }),
    [parts, demand, stations],
  );

  const baseline = useMemo(
    () => Object.fromEntries((template?.demandBaseline || []).map((d) => [d.partId, d.weeklyQty])),
    [template],
  );
  const partMeta = useMemo(() => Object.fromEntries(parts.map((p) => [p.id, p])), [parts]);

  // The sandbox-scenario shape, so save and dispatch both go through the shared builders.
  const config = () => ({
    demand: { ...demand },
    stations: { ...stations },
    cycleMult: {},
    castings: { ...castings },
    cycleOverrides: {},
    presetOverrides: {},
    policy: { xferMode: 'TRANSFER_BATCH', xferPct, edgeBatch: {}, realTime },
  });

  const scaleAll = (pct) => {
    setDemand(Object.fromEntries(Object.entries(baseline).map(([id, q]) => [id, Math.round(q * pct / 100)])));
  };
  const stepDemand = (id, dir) => {
    // Step by 10% of the part's BASELINE, not of its current value — a percentage of the current
    // value shrinks as you go down and never gets back to where it started.
    const step = Math.max(1, Math.round((baseline[id] || 100) * 0.1));
    setDemand((d) => ({ ...d, [id]: Math.max(0, (d[id] || 0) + dir * step) }));
  };

  const dispatch = async (scn) => {
    if (!template) return;
    setBusy(scn ? scn.id : 'dispatch'); setErr(null);
    try {
      const cfg = scn || config();
      await dispatchScenario(template.id, {
        company,
        demandOverride: demandOverrideFromSaved(cfg),
        policy: policyFromSaved(cfg),
      }, token);
      say(`Dispatched ${scn ? scn.name : 'this plan'} to ${company}`);
      onDispatched?.();
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Dispatch failed');
    } finally {
      setBusy(null);
    }
  };

  const resetDispatch = async () => {
    setBusy('reset'); setErr(null);
    try {
      const res = await axios.post('/machinesim/api/sim/dispatch-reset', { companies: [company] },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      const r = (res.data?.results || [])[0] || {};
      if (r.error) { setErr(r.error); return; }
      say(`${r.clearedCount ?? 0} cleared${r.skippedCount ? ` · ${r.skippedCount} already started` : ''}`);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Reset failed');
    } finally {
      setBusy(null);
    }
  };

  const doSave = async () => {
    const n = name.trim() || `Scenario ${saved.length + 1}`;
    setBusy('save');
    try {
      const row = await createSaved(company, { name: n, ...config() }, token);
      setSaved((list) => [...list, row]);
      setName(''); setNaming(false);
      say(`Saved “${n}” — available on every device`);
    } catch (e) {
      say(e?.response?.data?.error || e?.message || 'Save failed', 'err');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div style={{ padding: '56px 16px', textAlign: 'center', color: T.muted }}>Loading plan…</div>;
  if (!template) {
    return (
      <div style={{ padding: '56px 16px', textAlign: 'center', color: T.muted, fontSize: 13, lineHeight: 1.6 }}>
        No planning template for “{company}”.
        {err && <div style={{ color: T.danger, marginTop: 8 }}>{err}</div>}
      </div>
    );
  }

  const sev = cap.severity;
  const total = cap.total;
  // The largest lot is what the transfer % bites on hardest, so it's the honest number to quote for
  // "how long until the next stage starts". Approximate on purpose — the planner may split lots.
  const biggestLot = Math.max(0, ...Object.values(demand).map((q) => Number(q) || 0));
  const binSize = Math.max(1, Math.round(biggestLot * xferPct / 100));

  return (
    <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, alignItems: 'start' }}>

      {/* Verdict — the one thing that must be readable across a booth aisle. */}
      <Panel span>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1 }}>{total.toLocaleString('en-US')}</div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.muted, marginTop: 5 }}>
              parts this week
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: `var(${sev.colorVar})` }}>{sev.label}</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
              {cap.worst ? `${typeLabel(cap.worst.key)} at ${Math.round(cap.worst.util * 100)}%` : '—'}
            </div>
          </div>
        </div>
      </Panel>

      {err && <Panel span><div style={{ color: T.danger, fontSize: 13 }}>{err}</div></Panel>}

      {/* 1 — Demand */}
      <Panel title="Demand" cap="Weekly quantity per part. Steps are 10% of the template baseline.">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: 'auto' }}>
          {[50, 75, 100, 150].map((pct) => (
            <TouchBtn key={pct} variant="ghost" compact onPress={() => scaleAll(pct)}>{pct}%</TouchBtn>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Object.keys(baseline).map((id) => (
            <Stepper
              key={id}
              label={partMeta[id]?.partNumber || id}
              sub={`baseline ${Number(baseline[id] || 0).toLocaleString('en-US')}`}
              value={Number(demand[id] || 0)}
              onStep={(dir) => stepDemand(id, dir)}
            />
          ))}
        </div>
      </Panel>

      {/* 2 — Castings */}
      <Panel title="Castings on hand" cap="Starting inventory seeded at the first machining stage. This is what lets a lathe cut at t=0 instead of waiting on the foundry.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {Object.keys(baseline).map((id) => (
            <Stepper
              key={id}
              label={partMeta[id]?.partNumber || id}
              sub={`${Math.floor((Number(castings[id] || 0)) / binSize) || 0} lathe-bin${Math.floor((Number(castings[id] || 0)) / binSize) === 1 ? '' : 's'} covered`}
              value={Number(castings[id] || 0)}
              onStep={(dir) => setCastings((c) => ({ ...c, [id]: Math.max(0, (Number(c[id]) || 0) + dir * binSize) }))}
            />
          ))}
        </div>
        <div style={{ fontSize: 11.5, color: T.dimmed, marginTop: 10, lineHeight: 1.5 }}>
          Seeded in whole transfer bins of ~{binSize.toLocaleString('en-US')}. One bin starts one lathe;
          the seed feeds only the first machining stage, so mills still wait for real upstream parts.
        </div>
      </Panel>

      {/* 3 — Transfer batch. The most demo-visible number in the app. */}
      <Panel title="Transfer batch" cap="How much a stage finishes before the next stage may start. Smaller = the whole line lights up sooner.">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {[2, 5, 10, 25, 50].map((pct) => (
            <TouchBtn key={pct} variant={xferPct === pct ? 'gold' : 'ghost'} onPress={() => setXferPct(pct)}>
              {pct}%
            </TouchBtn>
          ))}
        </div>
        <div style={{ padding: 12, borderRadius: 11, background: T.surface2, fontSize: 13, lineHeight: 1.6 }}>
          ≈ <strong>{binSize.toLocaleString('en-US')} parts</strong> per bin on the largest lot.
          <div style={{ color: T.muted, fontSize: 12, marginTop: 4 }}>
            A downstream stage stays STARVED until its first bin fills. At ~106s per part that is
            roughly <strong style={{ color: T.text }}>{fmtDur(binSize * 106)}</strong> before the mills start.
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <Toggle
            on={realTime}
            onPress={() => setRealTime((v) => !v)}
            label="Real-time cycles"
            cap="Run each op at its program's full compiled length instead of an accelerated cycle — what makes the FOCAS block/line sweep look real."
          />
        </div>
      </Panel>

      {/* 4 — Capacity, read-only. */}
      <Panel title="Capacity" cap="Required work vs. what the roster runs in a 24/7 week. Station counts come from the plant and are not editable here.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          {cap.rows.map((r) => {
            const pct = Math.min(100, (r.util || 0) * 100 / 2);   // track shows up to 200%
            const col = r.util > 1 ? T.danger : r.util > 0.85 ? T.warning : T.success;
            return (
              <div key={r.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 5 }}>
                  <span style={{ fontWeight: 700 }}>{typeLabel(r.key)} <span style={{ color: T.dimmed, fontWeight: 500 }}>×{r.count}</span></span>
                  <span style={{ fontFamily: '"Courier New", monospace', color: col, fontWeight: 700 }}>
                    {isFinite(r.util) ? `${Math.round(r.util * 100)}%` : '∞'}
                  </span>
                </div>
                <div style={{ height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: col }} />
                  {/* 100% redline sits at the halfway mark of a 0–200% track. */}
                  <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.35)' }} />
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      {/* Commit */}
      <Panel span>
        <TouchBtn variant="gold" full busy={busy === 'dispatch'} confirm="Tap again to dispatch"
          onPress={() => dispatch(null)}>
          ▶ Dispatch this plan to the floor
        </TouchBtn>
        <div style={{ fontSize: 11.5, color: T.dimmed, marginTop: 8, lineHeight: 1.5 }}>
          Generates the scenario, plans it, commits it and puts the floor in planned mode. Work already
          running is not touched — use Reset dispatch first for a clean board.
        </div>

        <div style={{ marginTop: 14, display: 'flex', gap: 9 }}>
          {naming ? (
            <>
              <input autoFocus placeholder="Scenario name" value={name}
                onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
              <TouchBtn variant="ghost" busy={busy === 'save'} onPress={doSave}>Save</TouchBtn>
            </>
          ) : (
            <TouchBtn variant="ghost" full onPress={() => setNaming(true)}>💾 Save as scenario</TouchBtn>
          )}
        </div>
      </Panel>

      {/* Saved scenarios — one-tap relaunch of something already tuned. */}
      {saved.length > 0 && (
        <Panel span title="Saved scenarios" cap="Shared with the Capacity Sandbox across every device. Dispatching one here and there produces an identical plan.">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
            {saved.map((s) => (
              <div key={s.id} style={{ padding: 12, borderRadius: 12, background: T.surface2, border: `1px solid ${T.hairline}` }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>{s.name}</div>
                <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>
                  {Object.values(s.demand || {}).reduce((a, b) => a + Number(b || 0), 0).toLocaleString('en-US')} parts
                  {s.policy?.xferPct != null && ` · ${s.policy.xferPct}% transfer`}
                </div>
                <TouchBtn variant="ghost" full busy={busy === s.id} confirm="Tap again to dispatch"
                  onPress={() => dispatch(s)}>
                  ▶ Dispatch
                </TouchBtn>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Last, and separated — the only control here that takes work off the floor. */}
      <Panel span>
        <TouchBtn variant="ghost" full busy={busy === 'reset'} confirm="Tap again to reset dispatch"
          onPress={resetDispatch}>
          ⟳ Reset dispatch
        </TouchBtn>
        <div style={{ fontSize: 11.5, color: T.dimmed, marginTop: 8, lineHeight: 1.5 }}>
          Takes undispatched work back off the floor. Started work is left running. Nothing is deleted.
        </div>
      </Panel>
    </div>
  );
}

// "2h 54m" / "35m" — a duration a person judges a demo by, not a raw second count.
function fmtDur(sec) {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

// A labelled number with −/+ on either side. Both targets are 52px and sit at the OUTER edges of the
// row: the two controls a thumb alternates between should be as far apart as the row allows, so a
// mis-tap lands on nothing rather than on the opposite action.
function Stepper({ label, sub, value, onStep }) {
  const btn = {
    width: T.tap, height: T.tap, flexShrink: 0, borderRadius: 11, cursor: 'pointer',
    border: `1px solid rgba(168,144,96,0.32)`, background: 'transparent', color: T.text,
    fontSize: 22, fontWeight: 700, fontFamily: 'inherit', lineHeight: 1,
    touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button type="button" style={btn} onClick={() => onStep(-1)} aria-label={`decrease ${label}`}>−</button>
      <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: '"Courier New", monospace', lineHeight: 1.2 }}>
          {value.toLocaleString('en-US')}
        </div>
        {sub && <div style={{ fontSize: 11, color: T.dimmed }}>{sub}</div>}
      </div>
      <button type="button" style={btn} onClick={() => onStep(1)} aria-label={`increase ${label}`}>+</button>
    </div>
  );
}

function Toggle({ on, onPress, label, cap }) {
  return (
    <button
      type="button"
      onClick={onPress}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
        minHeight: T.tap, padding: '10px 13px', borderRadius: 11, cursor: 'pointer',
        background: 'transparent', border: `1px solid ${on ? T.gold : T.hairline}`,
        color: T.text, fontFamily: 'inherit', touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{
        width: 44, height: 26, flexShrink: 0, borderRadius: 999, position: 'relative',
        background: on ? T.gold : 'rgba(255,255,255,0.12)', transition: 'background .15s',
      }}>
        <span style={{
          position: 'absolute', top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: '50%',
          background: on ? T.onFilled : T.muted, transition: 'left .15s',
        }} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{label}</span>
        {cap && <span style={{ display: 'block', fontSize: 11.5, color: T.muted, lineHeight: 1.45, marginTop: 2 }}>{cap}</span>}
      </span>
    </button>
  );
}

// Card wrapper for the Plan tab. `span` makes it break the grid and run full width — used for the
// verdict and the commit block, which are decisions about the whole plan rather than one lever.
function Panel({ title, cap, span, children }) {
  return (
    <div style={{
      gridColumn: span ? '1 / -1' : undefined,
      background: T.surface, border: `1px solid ${T.hairline}`, borderRadius: 14, padding: 14,
    }}>
      {title && <div style={{ fontSize: 15, fontWeight: 800, marginBottom: cap ? 4 : 12 }}>{title}</div>}
      {cap && <div style={{ fontSize: 11.5, color: T.muted, lineHeight: 1.5, marginBottom: 12 }}>{cap}</div>}
      {children}
    </div>
  );
}

// ── Primitives ───────────────────────────────────────────────────────────────

const VARIANTS = {
  gold:    { bg: T.gold,    fg: T.onFilled, bd: T.gold },
  success: { bg: T.success, fg: '#eafff6',  bd: T.success },
  warning: { bg: T.warning, fg: T.onFilled, bd: T.warning },
  danger:  { bg: T.danger,  fg: '#fff1ef',  bd: T.danger },
  ghost:   { bg: 'transparent', fg: T.text, bd: 'rgba(168,144,96,0.32)' },
};

/**
 * The only button on this page.
 *
 * `confirm` arms rather than opening a modal: first tap swaps the label for the confirm copy and
 * turns the button amber, second tap fires, and it disarms itself after 4s so an armed button is
 * never left lying in wait for an unrelated tap later. `busy` shows in place so a slow round-trip
 * cannot read as a dead tap and earn a second one.
 */
function TouchBtn({ children, onPress, variant = 'ghost', full, compact, disabled, busy, confirm, as = 'button' }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(id);
  }, [armed]);

  const v = VARIANTS[armed ? 'warning' : variant] || VARIANTS.ghost;
  const inert = disabled || busy;

  const handle = () => {
    if (inert) return;
    if (confirm && !armed) { setArmed(true); return; }
    setArmed(false);
    onPress?.();
  };

  const style = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    width: full ? '100%' : undefined,
    minHeight: compact ? 40 : T.tap,
    padding: compact ? '0 14px' : '0 16px',
    borderRadius: 11,
    border: `1px solid ${v.bd}`,
    background: v.bg,
    color: v.fg,
    fontFamily: 'inherit',
    fontSize: compact ? 13 : 14.5,
    fontWeight: 700,
    letterSpacing: '0.01em',
    lineHeight: 1.2,
    textAlign: 'center',
    cursor: inert ? 'default' : 'pointer',
    opacity: inert ? 0.45 : 1,
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
  };

  if (as === 'span') return <span style={style}>{children}</span>;
  return (
    <button type="button" style={style} disabled={inert} onClick={handle}>
      {busy ? '…' : armed ? confirm : children}
    </button>
  );
}

function PickRow({ selected, onPress, left, title, sub, tone }) {
  return (
    <button
      type="button"
      onClick={onPress}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
        minHeight: T.tap, padding: '10px 13px', borderRadius: 11, cursor: 'pointer',
        background: selected ? T.surface2 : 'transparent',
        border: `1px solid ${selected ? T.gold : T.hairline}`,
        color: T.text, fontFamily: 'inherit', touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{ fontSize: 17, color: tone, flexShrink: 0 }}>{left}</span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{title}</span>
        {sub && <span style={{ display: 'block', fontSize: 12, color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>}
      </span>
    </button>
  );
}

// Bottom sheet, not a centred modal: on a held tablet the bottom half of the screen is where the
// thumbs are, and a sheet anchored there needs no reach across the glass. Scrim tap closes; the
// panel stops propagation so a tap inside never dismisses it mid-choice.
function Sheet({ title, children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.62)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 620, maxHeight: '86vh', overflowY: 'auto',
          background: T.surface, border: `1px solid ${T.hairline}`,
          borderTopLeftRadius: 18, borderTopRightRadius: 18,
          padding: '14px 16px calc(20px + env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 -18px 50px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.18)', margin: '0 auto 14px' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <span style={{ fontSize: 17, fontWeight: 800, color: T.text, minWidth: 0 }}>{title}</span>
          <TouchBtn variant="ghost" compact onPress={onClose}>Close</TouchBtn>
        </div>
        {children}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{
      flexShrink: 0, minWidth: 88, padding: '9px 14px', borderRadius: 11,
      background: T.surface, border: `1px solid ${T.hairline}`,
    }}>
      <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.muted, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Toast({ toast }) {
  return (
    <div style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)',
      bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', zIndex: 200,
      maxWidth: '92vw', padding: '13px 18px', borderRadius: 12,
      background: T.surface2, border: `1px solid ${toast.tone === 'err' ? T.danger : T.success}`,
      color: toast.tone === 'err' ? '#f2b8b0' : T.text,
      fontSize: 13.5, fontWeight: 600, boxShadow: '0 12px 34px rgba(0,0,0,0.5)',
    }}>
      {toast.text}
    </div>
  );
}

const inputStyle = {
  minHeight: T.tap, padding: '0 14px', borderRadius: 11,
  border: `1px solid ${T.hairline}`, background: T.surface2, color: T.text,
  fontFamily: 'inherit', fontSize: 15, width: '100%', boxSizing: 'border-box',
};
